import React, { useCallback } from 'react';
import cn from 'classnames';

import ICO_CLOSE from 'assets/images/close-button.svg';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import PERM from 'constants/core/perm.const';
import IN from 'constants/in';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';

import ConfirmationModal from 'common/ConfirmationModal';

import CN from './ServiceListItem.module.scss';

const L = logger('ServiceListItem');

const ServiceListItem = ({ itemName, errorMessage, isSaved, expandable, onEdit = alwaysNoop, onDelete = alwaysNoop, quickSetupLayout }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const { val: isModalOpen, yay: showModal, nay: hideModal } = useTip(false);

  const handleExpand = useCallback(() => {
    if (!expandable) return;

    call('ServiceListItem.onEdit', onEdit);
  }, [expandable, onEdit]);

  const onConfirmDelete = useCallback(() => {
    if (!expandable) return;

    hideModal();
    call('ServiceListItem.onDelete', onDelete);
  }, [expandable, hideModal, onDelete]);

  L.debug('render()', itemName, { isSaved, quickSetupLayout, errorMessage, isModalOpen });

  return (
    <div
      data-bem="ServiceListItem"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      {fromAnyToBool(isSaved) && (
        <div className="alert success animated fadeInDown">
          {t('common.saved')}
          <span className="close">
            <img src={ICO_CLOSE} alt="Close modal" />
          </span>
        </div>
      )}
      {isModalOpen && (
        <ConfirmationModal
          popupTitle={t('common.buttons.deleteUpp')}
          popupMessage={t('composite.categoryItem.confirmationModalMsg', { item: itemName })}
          leftButtonText={t('common.buttons.no')}
          rightButtonText={t('common.buttons.yes')}
          leftButtonCallback={hideModal}
          rightButtonCallback={onConfirmDelete}
        />
      )}
      {
        <div className={CN.panel} onClick={handleExpand}>
          <div className={CN.container}>
            <div className={CN.dragIcon}>
              <Icon variant="drag" width="lg" />
            </div>
            <div className={CN.categoryName}>{itemName}</div>
          </div>

          {!quickSetupLayout && (
            <div className={CN.buttonsContainer}>
              <div className={CN.buttonGap}>
                <Button
                  variant="slim"
                  width="small"
                  onClick={() => {
                    onEdit();
                  }}
                >
                  {t('common.buttons.editUpp')}
                </Button>
              </div>

              <Button variant="bin" onClick={showModal}>
                <Icon variant="delete" width="lg" />
              </Button>

              <TooltipForReceptionist permission={PERM.canEditServices} where={IN.servicesDropDelete} />
            </div>
          )}
        </div>
      }
      {errorMessage && <div>{errorMessage}</div>}
    </div>
  );
};

export default ServiceListItem;
